import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';

import { exportToCsv } from '@verantos-dev/shared-components';

import type { FlattenedFinalResultRow } from './useFlattenedFinalResults';

/**
 * Custom hook to handle CSV export of final mapping results
 *
 * This hook provides the export functionality for downloading the flattened
 * final results table as a CSV file.
 *
 * FEATURES:
 * - Uses the shared exportToCsv utility
 * - Accepts pre-computed flattened rows for efficiency
 * - Generates CSV with proper column headers
 * - Triggers browser download
 *
 * USAGE:
 * ```tsx
 * const { handleExport } = useExportFinalResults({
 *   flatRows,
 *   columns,
 * });
 *
 * // Later, when user confirms filename:
 * handleExport('my-mappings-2025');
 * ```
 */

interface UseExportFinalResultsProps {
  /** Pre-computed flattened rows to export */
  flatRows: FlattenedFinalResultRow[];
  /** Column definitions for CSV headers */
  columns: GridColDef<FlattenedFinalResultRow>[];
}

interface UseExportFinalResultsReturn {
  /** Function to trigger CSV export with given filename */
  handleExport: (filename: string) => void;
}

export const useExportFinalResults = ({
  flatRows,
  columns,
}: UseExportFinalResultsProps): UseExportFinalResultsReturn => {
  const handleExport = useCallback(
    (filename: string) => {
      if (!flatRows || flatRows.length === 0) {
        console.warn('No data to export');
        return;
      }

      // Transform rows to convert standard values to "Yes" or "No"
      const transformedRows = flatRows.map((row) => {
        // Create a copy of the row
        const transformedRow = { ...row };

        // Transform standard value based on row type and status
        if (transformedRow.type === 'Mapped' || transformedRow.type === 'Both') {
          // Mapped rows and Both rows (which are both Source and Mapped) should always be "Yes"
          transformedRow.standard = 'Yes';
        } else if (transformedRow.type === 'Source') {
          // Source rows should be "Yes" only if Status is "Already standard"
          if (
            transformedRow.status &&
            transformedRow.status.toString().toLowerCase() === 'already standard'
          ) {
            transformedRow.standard = 'Yes';
          } else {
            transformedRow.standard = 'No';
          }
        }

        return transformedRow;
      });

      // Map GridColDef to the format expected by exportToCsv
      const csvColumns = columns.map((col) => ({
        field: col.field as keyof FlattenedFinalResultRow,
        headerName: col.headerName || String(col.field),
      }));

      // Add .csv extension if not present
      const csvFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;

      // Trigger CSV download with transformed rows
      exportToCsv(csvFilename, transformedRows, csvColumns);
    },
    [flatRows, columns]
  );

  return { handleExport };
};
